/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the base layout for the browse view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI Paper component.
 */
import Paper from '@material-ui/core/Paper';

/**
 * Material UI List component.
 */
import List from '@material-ui/core/List';

/**
 * Material UI Breadcrumbs component.
 */
import Breadcrumbs from '@material-ui/core/Breadcrumbs';

/**
 * Material UI Typography component.
 */
import { Typography } from '@material-ui/core';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of version data loaded from the ServiceStack API.
 */
import { useVersionSelector, VersionSelectors } from 'src/store/reducers/version';

/**
 * Enables the use of category data loaded from the ServiceStack API.
 */
import useCategories from 'src/hooks/useCategories';

/**
 * Enumeration
 */
import { RequestState } from 'src/store/reducers';

/**
 * used to render the view for each category item.
 */
import CategoryListItem from 'src/components/CategoryListItem';

/**
 * Loading indicator
 */
import Loading from 'src/components/Loading';

/**
 * Used to limit results to the current version only.
 */
import useVersionCheck from 'src/hooks/useVersionCheck';

/**
 * used to update app state and post messages to opener window.
 */
import useAppState from 'src/hooks/useAppState';

/**
 * Enumeration
 */
import { WindowView } from 'src/utils/MessageManager';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    title: {
        margin: theme.spacing(3, 0)
    },
    paper: {
        margin: theme.spacing(3, 0)
    },
    list: {
        width: '100%',
        padding: theme.spacing(0,0)
    },
    breadcrumbs: {
        margin: theme.spacing(3, 0)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

// const limit = 5;

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const BrowseCategories: React.FunctionComponent = ({

}) => {
    const classes = useStyles();
    const currentVersion = useVersionSelector(VersionSelectors.selectVersion);

    if (!currentVersion) {
        return (
            <Loading />
        )
    }

    const [version, view, loadState] = useCategories(currentVersion.id);

    useVersionCheck(version);

    useAppState(
        WindowView.Browse,
        version,
        null,
        null,
        null,
        null
    );

    return (
        <>
            <Breadcrumbs
                separator="›"
                aria-label="Breadcrumb"
                className={classes.breadcrumbs}
            >
                <Typography color="textPrimary">
                    Categories
                </Typography>
            </Breadcrumbs>
            <Paper
                className={classes.paper}
            >
                {
                    loadState && loadState.state === RequestState.Pending && (
                        <Loading />
                    )
                }
                <List
                    className={classes.list}
                >
                    {
                        version && view.map((categoryId, index) => (
                            <CategoryListItem
                                key={categoryId}
                                version={version}
                                categoryId={categoryId}
                                showDivider={index !== categoryId}
                            />
                        ))
                    }
                </List>
            </Paper>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default BrowseCategories;


// WEBPACK FOOTER //
// ./src/containers/BrowseCategories.tsx